interface NetworkError extends Error {
  message: string;
}

const isOnline = (): boolean => {
  return typeof navigator !== 'undefined' && navigator.onLine;
};

export const isNetworkError = (error: NetworkError): boolean => {
  return (
    !isOnline() ||
    error.message.includes('Failed to fetch') ||
    error.message.includes('Network Error') ||
    error.message.includes('could not establish connection') ||
    error.message.includes('internet connection') ||
    error.message.includes('ERR_INTERNET_DISCONNECTED') ||
    error.message.includes('ERR_NETWORK_CHANGED')
  );
};

export const handleNetworkError = async <T>(
  operation: () => Promise<T>,
  retries = 3,
  delay = 1000
): Promise<T> => {
  try {
    return await operation();
  } catch (error) {
    if (error instanceof Error && isNetworkError(error) && retries > 0) {
      await new Promise(resolve => setTimeout(resolve, delay));
      return handleNetworkError(operation, retries - 1, delay * 2);
    }
    throw error;
  }
};

let isOffline = !navigator.onLine;
let offlineListeners: ((offline: boolean) => void)[] = [];
let reconnectCallbacks: (() => void)[] = [];

export const networkUtils = {
  init() {
    // If the browser comes back online, run all the reconnect callbacks
    window.addEventListener('online', () => {
      const wasOfflineBefore = isOffline;
      isOffline = false;
      
      // Notify listeners of connectivity change
      offlineListeners.forEach(listener => listener(false));
      
      // If we were offline before, trigger reconnection logic
      if (wasOfflineBefore) {
        console.log('Network reconnected. Refreshing data...');
        reconnectCallbacks.forEach(callback => callback());
      }
    });
    
    window.addEventListener('offline', () => {
      isOffline = true;
      offlineListeners.forEach(listener => listener(true));
      console.log('Network disconnected. App will use cached data and reconnect automatically when online.');
    });
    
    // Set initial state
    isOffline = !navigator.onLine;
  },

  isOffline() {
    return isOffline;
  },

  onConnectivityChange(listener: (offline: boolean) => void) {
    offlineListeners.push(listener);
    return () => {
      offlineListeners = offlineListeners.filter(l => l !== listener);
    };
  },
  
  // Add a new function for callbacks to run when connection is restored
  onReconnect(callback: () => void) {
    reconnectCallbacks.push(callback);
    return () => {
      reconnectCallbacks = reconnectCallbacks.filter(c => c !== callback);
    };
  },
  
  // Manually check connection status (useful for components to check on mount)
  checkConnection() {
    return navigator.onLine;
  },
  
  // Record that we've experienced a network error
  markConnectionProblem() {
    // No-op: Network error tracking removed (wasOffline was write-only)
  }
}; 